function parseCountdownDateTime(value) {
  if (!value) return null;
  const normalized = String(value).trim().replace('T', ' ');
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})$/);
  if (!match) return null;

  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5])
  };
}

function isValidTimeZone(timeZone) {
  try {
    Intl.DateTimeFormat('en-US', { timeZone }).format(new Date());
    return true;
  } catch (error) {
    return false;
  }
}

function getTimeZoneOffsetMs(timestamp, timeZone) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });

  const parts = formatter.formatToParts(new Date(timestamp));
  const values = {};
  parts.forEach((part) => {
    if (part.type !== 'literal') values[part.type] = part.value;
  });

  const asUtc = Date.UTC(
    Number(values.year),
    Number(values.month) - 1,
    Number(values.day),
    Number(values.hour),
    Number(values.minute),
    Number(values.second)
  );

  return asUtc - timestamp;
}

function zonedDateTimeToUtcTimestamp(parts, timeZone) {
  const baseUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, 0);
  let guess = baseUtc;

  for (let i = 0; i < 3; i += 1) {
    const offset = getTimeZoneOffsetMs(guess, timeZone);
    guess = baseUtc - offset;
  }

  return guess;
}

function updateCountdownDigits(container, timeRemainingMs) {
  const totalSeconds = Math.max(0, Math.floor(timeRemainingMs / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const values = {
    days: String(days).padStart(2, '0'),
    hours: String(hours).padStart(2, '0'),
    minutes: String(minutes).padStart(2, '0'),
    seconds: String(seconds).padStart(2, '0')
  };

  Object.keys(values).forEach((key) => {
    const part = container.querySelector(`[data-countdown-part="${key}"] span`);
    if (!part) return;
    part.textContent = values[key];
    part.style.transform = 'translateY(0%)';
  });
}

function clearCountdownIntervals(container) {
  if (!container) return;
  if (container.__countdownInterval) {
    clearInterval(container.__countdownInterval);
    container.__countdownInterval = null;
  }
  if (container.__countdownIntervalFast) {
    clearInterval(container.__countdownIntervalFast);
    container.__countdownIntervalFast = null;
  }
  if (container.__countdownBounceTimeout) {
    clearTimeout(container.__countdownBounceTimeout);
    container.__countdownBounceTimeout = null;
  }
}

function clearAllCountdownIntervals() {
  if (!window.__cartDrawerCountdownContainers) return;
  window.__cartDrawerCountdownContainers.forEach((container) => clearCountdownIntervals(container));
  window.__cartDrawerCountdownContainers = [];
}

function initSingleCartDrawerCountdownLegacy(container) {
  if (!container) return;

  clearCountdownIntervals(container);

  const rawStart = container.dataset.countdownStart;
  const rawEnd = container.dataset.countdownEnd;
  const rawTimeZone = container.dataset.countdownTimezone || 'UTC';
  const hideExpired = container.dataset.countdownHideExpired !== 'false';
  const timeZone = isValidTimeZone(rawTimeZone) ? rawTimeZone : 'UTC';

  const startParts = parseCountdownDateTime(rawStart);
  const endParts = parseCountdownDateTime(rawEnd);
  if (!startParts || !endParts) {
    container.classList.add('hidden');
    return;
  }

  const startTimestamp = zonedDateTimeToUtcTimestamp(startParts, timeZone);
  const endTimestamp = zonedDateTimeToUtcTimestamp(endParts, timeZone);
  if (!Number.isFinite(startTimestamp) || !Number.isFinite(endTimestamp) || endTimestamp <= startTimestamp) {
    container.classList.add('hidden');
    return;
  }

  const tick = () => {
    const now = Date.now();

    if (now < startTimestamp) {
      container.classList.add('hidden');
      return;
    }

    if (now >= endTimestamp) {
      updateCountdownDigits(container, 0);
      if (hideExpired) container.classList.add('hidden');
      clearCountdownIntervals(container);
      return;
    }

    container.classList.remove('hidden');
    updateCountdownDigits(container, endTimestamp - now);
  };

  tick();
  container.__countdownInterval = setInterval(tick, 1000);
}

function updateLotteryDigits(topEl, timeRemainingMs) {
  const totalSeconds = Math.max(0, Math.floor(timeRemainingMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const hrText = hours >= 100 ? String(hours) : String(hours).padStart(2, '0');
  const minText = String(minutes).padStart(2, '0');
  const secText = String(seconds).padStart(2, '0');

  const hrSpan = topEl.querySelector('[data-countdown-lottery-part="hr"] span');
  const minSpan = topEl.querySelector('[data-countdown-lottery-part="min"] span');
  const secSpan = topEl.querySelector('[data-countdown-lottery-part="sec"] span');

  if (hrSpan) hrSpan.textContent = hrText;
  if (minSpan) minSpan.textContent = minText;
  if (secSpan) secSpan.textContent = secText;

  return { secText };
}

/** Lottery SSR can be in-window while the client is past end; remove layout class and inner scroll wrapper so non-lottery cart CSS applies. */
function flattenCartDrawerLotteryLayout(host) {
  if (!host || !host.classList.contains('cart-drawer-items--lottery-layout')) return;
  const scroll = host.querySelector(':scope > .cart-drawer-items__scroll');
  if (scroll) {
    while (scroll.firstChild) {
      host.insertBefore(scroll.firstChild, scroll);
    }
    scroll.remove();
  }
  host.classList.remove('cart-drawer-items--lottery-layout');
}

function initSingleCartDrawerCountdownLottery(topEl) {
  if (!topEl) return;

  clearCountdownIntervals(topEl);

  const bottomEl = topEl.parentElement && topEl.parentElement.querySelector('[data-countdown-lottery-bottom]');
  const host = topEl.closest('cart-drawer-items');
  const drawer = topEl.closest('cart-drawer');
  const inner = topEl.closest('.drawer__inner');

  // Never run lottery mode for empty carts.
  if ((drawer && drawer.classList.contains('is-empty')) || (host && host.classList.contains('is-empty'))) {
    topEl.classList.add('hidden');
    if (bottomEl) bottomEl.classList.add('hidden');
    if (host) host.classList.remove('cart-drawer-items--lottery-active');
    if (inner) inner.classList.remove('drawer__inner--lottery');
    return;
  }

  const rawStart = topEl.dataset.countdownStart;
  const rawEnd = topEl.dataset.countdownEnd;
  const rawTimeZone = topEl.dataset.countdownTimezone || 'UTC';
  const hideExpired = topEl.dataset.countdownHideExpired !== 'false';
  const timeZone = isValidTimeZone(rawTimeZone) ? rawTimeZone : 'UTC';

  const startParts = parseCountdownDateTime(rawStart);
  const endParts = parseCountdownDateTime(rawEnd);
  if (!startParts || !endParts) {
    topEl.classList.add('hidden');
    if (bottomEl) bottomEl.classList.add('hidden');
    if (host) host.classList.remove('cart-drawer-items--lottery-active');
    return;
  }

  const startTimestamp = zonedDateTimeToUtcTimestamp(startParts, timeZone);
  const endTimestamp = zonedDateTimeToUtcTimestamp(endParts, timeZone);
  if (!Number.isFinite(startTimestamp) || !Number.isFinite(endTimestamp) || endTimestamp <= startTimestamp) {
    topEl.classList.add('hidden');
    if (bottomEl) bottomEl.classList.add('hidden');
    if (host) host.classList.remove('cart-drawer-items--lottery-active');
    return;
  }

  const setVisible = (isVisible) => {
    if (isVisible) {
      topEl.classList.remove('hidden');
      if (bottomEl) bottomEl.classList.remove('hidden');
      if (host) host.classList.add('cart-drawer-items--lottery-active');
      if (inner) inner.classList.add('drawer__inner--lottery');
      return;
    }
    topEl.classList.add('hidden');
    if (bottomEl) bottomEl.classList.add('hidden');
    if (host) host.classList.remove('cart-drawer-items--lottery-active');
    if (inner) inner.classList.remove('drawer__inner--lottery');
  };

  let lastSecText = null;

  const tick = () => {
    const now = Date.now();

    if (now < startTimestamp) {
      setVisible(false);
      updateLotteryDigits(topEl, 0);
      return;
    }

    if (now >= endTimestamp) {
      updateLotteryDigits(topEl, 0);
      if (hideExpired) {
        setVisible(false);
        flattenCartDrawerLotteryLayout(host);
      }
      clearCountdownIntervals(topEl);
      return;
    }

    const remainingMs = endTimestamp - now;
    const { secText } = updateLotteryDigits(topEl, remainingMs);

    if (secText && secText !== lastSecText) {
      lastSecText = secText;
      const secDigit = topEl.querySelector('[data-countdown-lottery-part="sec"]');
      if (secDigit) {
        secDigit.classList.remove('is-bounce');
        void secDigit.offsetWidth;
        secDigit.classList.add('is-bounce');
        if (topEl.__countdownBounceTimeout) clearTimeout(topEl.__countdownBounceTimeout);
        topEl.__countdownBounceTimeout = setTimeout(() => secDigit.classList.remove('is-bounce'), 300);
      }
    }

    setVisible(true);
  };

  setVisible(false);
  updateLotteryDigits(topEl, 0);
  topEl.__countdownInterval = setInterval(tick, 1000);
  tick();
}

function initCartDrawerCountdown() {
  clearAllCountdownIntervals();
  window.__cartDrawerCountdownContainers = [];

  // Safety: never show lottery layout on empty carts.
  document.querySelectorAll('cart-drawer').forEach((drawer) => {
    const host = drawer.querySelector('cart-drawer-items');
    const isEmptyDrawer = drawer.classList.contains('is-empty');
    const isEmptyHost = host && host.classList.contains('is-empty');
    const hasLineItems = !!drawer.querySelector('#CartDrawer-CartItems .cart-item');
    const isEmpty = isEmptyDrawer || isEmptyHost || !hasLineItems;

    if (!isEmpty) return;

    const inner = drawer.querySelector('.drawer__inner');
    if (inner) inner.classList.remove('drawer__inner--lottery');
    if (host) {
      flattenCartDrawerLotteryLayout(host);
      host.classList.remove('cart-drawer-items--lottery-active');
    }
    drawer.querySelectorAll('[data-countdown-mode="lottery"], [data-countdown-lottery-bottom]').forEach((el) => {
      el.classList.add('hidden');
    });
  });

  document.querySelectorAll('[data-cart-drawer-countdown]').forEach((container) => {
    const mode = container.dataset.countdownMode || 'legacy';
    window.__cartDrawerCountdownContainers.push(container);

    if (mode === 'lottery') {
      const host = container.closest('cart-drawer-items');
      const drawer = container.closest('cart-drawer');
      const inner = container.closest('.drawer__inner');
      const bottomEl = host && host.querySelector('[data-countdown-lottery-bottom]');
      const isEmpty = (drawer && drawer.classList.contains('is-empty')) || (host && host.classList.contains('is-empty'));

      if (isEmpty) {
        container.classList.add('hidden');
        if (bottomEl) bottomEl.classList.add('hidden');
        if (host) host.classList.remove('cart-drawer-items--lottery-active');
        if (inner) inner.classList.remove('drawer__inner--lottery');
        return;
      }

      initSingleCartDrawerCountdownLottery(container);
      return;
    }

    initSingleCartDrawerCountdownLegacy(container);
  });
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    setTimeout(() => { this.classList.add('animate', 'active') });

    document.body.classList.add('overflow-hidden');

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });
  }



  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHtml = parsedState.sections[section.id];

      if (!sectionElement || !sectionHtml) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
    }));

    setTimeout(() => {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }
    this.open();
    initCartDrawerCountdown();

    // ✅ Wait a moment before fetching cart to ensure sections are fully re-rendered
    setTimeout(() => {
      fetch('/cart.js')
        .then(res => res.json())
        .then(cart => {
          if (typeof updateGiftProgressBar === 'function') {
            updateGiftProgressBar(cart);
          }
        })
        .catch(() => {});
    }, 250); // Give time for prices + DOM to reflect
  });


  }




  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const target = parsed.querySelector(selector);
    if (target) return target.innerHTML;

    const fallback = parsed.querySelector('.shopify-section');
    if (fallback) return fallback.innerHTML;

    return parsed.body ? parsed.body.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
    console.log('22');
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

document.addEventListener('DOMContentLoaded', initCartDrawerCountdown);

